// src/components/common/LanguageSwitcher.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { languages } from '../../config/languageConfig'; // UPDATED: Import from centralized config

interface LanguageSwitcherProps {
    isInFooter?: boolean; // Prop to determine if used in footer
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ isInFooter = false }) => {
    const { i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isOpen = Boolean(anchorEl);

    // Find current language, defaulting to zh-TW
    const currentLanguage = languages.find(lang => i18n.language.startsWith(lang.code.split('-')[0])) || languages[1];

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = (langCode: string) => {
        i18n.changeLanguage(langCode);
        handleClose();
    };

    return (
        <>
            <Button
                color="inherit"
                onClick={handleOpen}
                startIcon={<LanguageIcon />}
                endIcon={<ExpandMoreIcon />}
                sx={{ textTransform: 'none' }}
                aria-controls={isOpen ? 'language-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isOpen ? 'true' : undefined}
            >
                <Typography variant="body2" sx={{ display: { xs: 'none', md: 'inline' } }}>
                    {currentLanguage.name}
                </Typography>
                <Typography variant="body2" sx={{ display: { xs: 'inline', md: 'none' } }}>
                    {currentLanguage.short}
                </Typography>
            </Button>
            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'language-button',
                }}
                anchorOrigin={isInFooter ? { vertical: 'top', horizontal: 'left' } : { vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={isInFooter ? { vertical: 'bottom', horizontal: 'left' } : { vertical: 'top', horizontal: 'left' }}
            >
                {languages.map((lang) => (
                    <MenuItem
                        key={lang.code}
                        selected={i18n.language === lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                    >
                        {lang.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default LanguageSwitcher;